/**
 * @file MonthCalendar.js
 * @version 2.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-10-02
 * @modified 2026-10-03
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import './MonthCalendar.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
// Weeks start on Monday (UK).
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const pad = (n) => String(n).padStart(2, '0');
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Each event on every local day it covers. The end is exclusive (an all-day
// event ends at the next midnight), so step back a millisecond from it.
function eventsByDay(events) {
  const map = new Map();
  for (const ev of events || []) {
    const start = new Date(ev.start);
    const end = new Date(ev.end);
    if (Number.isNaN(+start) || Number.isNaN(+end)) continue;
    const last = new Date(Math.max(+start, +end - 1));
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    for (let n = 0; d <= last && n < 62; n++, d.setDate(d.getDate() + 1)) {
      const k = dayKey(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    }
  }
  return map;
}

function eventTime(ev, day) {
  if (ev.allDay) return 'All day';
  const s = new Date(ev.start);
  const e = new Date(ev.end);
  const from = dayKey(s) === dayKey(day) ? hhmm(s) : '…';
  const to = dayKey(new Date(+e - 1)) === dayKey(day) ? hhmm(e) : '…';
  return `${from}–${to}`;
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/**
 * @param {Array}    [events]        { id, subject, start, end, allDay, location }
 * @param {Function} [onRangeChange] (start, end) of the visible grid, end exclusive
 * @param {string}   [error]         shown as a warning in the header
 */
function MonthCalendar({ events, onRangeChange, error }) {
  const [shown, setShown] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const year = shown.getFullYear();
  const month = shown.getMonth();
  const today = new Date();
  const onTodaysMonth = year === today.getFullYear() && month === today.getMonth();

  // Whole weeks only, padded with the neighbouring months' days. A month spans
  // 4 to 6 weeks; the grid is sized from that count (--weeks), so a six-week
  // month fits the widget instead of overflowing it.
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weeks = Math.ceil((lead + daysInMonth) / 7);
  const cells = Array.from({ length: weeks * 7 }, (_, i) => new Date(year, month, i - lead + 1));

  const gridStart = cells[0];
  const gridEnd = new Date(year, month, weeks * 7 - lead + 1);
  useEffect(() => {
    if (onRangeChange) onRangeChange(gridStart, gridEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, onRangeChange]);

  const byDay = useMemo(() => eventsByDay(events), [events]);
  // The day's list fades in as the month fades out (0.4s, in the CSS), and back.
  // openDay stays set through the fade-out so the list still has its content.
  const [openDay, setOpenDay] = useState(null);
  const [agendaShown, setAgendaShown] = useState(false);
  const closeTimer = useRef(null);
  const openAgenda = (d) => {
    clearTimeout(closeTimer.current);
    setOpenDay(d);
    // Mounted transparent first, then made visible, so the fade-in runs.
    requestAnimationFrame(() => requestAnimationFrame(() => setAgendaShown(true)));
  };
  const closeAgenda = () => {
    setAgendaShown(false);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDay(null), 400);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const openEvents = openDay ? (byDay.get(dayKey(openDay)) || []) : [];

  const go = (delta) => setShown(new Date(year, month + delta, 1));
  const goToday = () => setShown(new Date(today.getFullYear(), today.getMonth(), 1));

  // The list sits beside the month, not inside it, so fading the month out
  // does not take the list with it. Both are positioned in the card body.
  return (
    <>
      {openDay && (
        <div className={`calendar-agenda${agendaShown ? ' shown' : ''}`}>
          <div className="calendar-agenda-header">
            <span>
              {openDay.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
            <button type="button" className="calendar-nav" onClick={closeAgenda} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <ul className="calendar-agenda-list">
            {openEvents.map((ev) => (
              <li key={ev.id || ev.subject}>
                <span className="calendar-agenda-time">{eventTime(ev, openDay)}</span>
                <span className="calendar-agenda-subject">
                  {ev.subject}
                  {ev.location && <span className="calendar-agenda-location">{ev.location}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={`month-calendar${agendaShown ? ' faded' : ''}`}>
        <div className="calendar-header">
          <div className="calendar-title">
            <span className="calendar-month">{MONTHS[month]}</span>
            <span className="calendar-year">{year}</span>
          </div>
          <div className="calendar-controls">
            {error && (
              <span className="calendar-error material-symbols-rounded" title={`Calendar could not be read: ${error}`}>
                sync_problem
              </span>
            )}
            <button
              type="button"
              className={`calendar-today-btn${onTodaysMonth ? ' hidden' : ''}`}
              onClick={goToday}
              tabIndex={onTodaysMonth ? -1 : 0}
              title="Back to this month"
            >
              Today
            </button>
            <button type="button" onClick={() => go(-1)} className="calendar-nav" title="Previous month">
              <span className="material-symbols-rounded">chevron_left</span>
            </button>
            <button type="button" onClick={() => go(1)} className="calendar-nav" title="Next month">
              <span className="material-symbols-rounded">chevron_right</span>
            </button>
          </div>
        </div>

        <div className="calendar-grid" style={{ '--weeks': weeks }}>
          {WEEKDAYS.map((d, i) => (
            <div key={`w${i}`} className={`calendar-weekday${i >= 5 ? ' weekend' : ''}`}>{d}</div>
          ))}
          {cells.map((d, i) => {
            const classes = ['calendar-day'];
            if (d.getMonth() !== month) classes.push('outside');
            if (i % 7 >= 5) classes.push('weekend');
            if (sameDay(d, today)) classes.push('today');
            const dayEvents = byDay.get(dayKey(d)) || [];
            if (dayEvents.length) classes.push('has-events');
            return (
              <div
                key={i}
                className={`calendar-cell${dayEvents.length ? ' clickable' : ''}`}
                onClick={dayEvents.length ? () => openAgenda(d) : undefined}
                title={dayEvents.length
                  ? dayEvents.map(ev => `${eventTime(ev, d)}  ${ev.subject}`).join('\n')
                  : undefined}
              >
                <span className={classes.join(' ')}>
                  {d.getDate()}
                  {dayEvents.length > 0 && (
                    <span className="calendar-dots">
                      {dayEvents.slice(0, 3).map((ev, n) => <i key={n} />)}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default MonthCalendar;
